import {useIntl} from "react-intl";
import DocumentsGroup from "./DocumentsGroup";
import {Fragment, useContext} from "react";
import EmptyStatePlaceholder from "@pages/WorksitesDocuments/EmptyStatePlaceholder";
import {SECTIONS_PLACEHOLDERS} from "@pages/WorksitesDocuments/utils/constants";
import {useHistory} from "react-router-dom";
import CountryContext from "../../CountryContext";
import {DragDropContext, Droppable, Draggable} from "react-beautiful-dnd";
import Placeholder from "@ui-components/Placeholder";
import Checkbox from "../../ui-components/Checkbox";

const DocumentsSection = (
	{
		worksiteId,
		section,
		sectionKey,
		onDocumentSelected,
		cancelSelection,
		onSelectAllSectionDocuments,
		onSelectAllGroupDocuments,
		setShowEditGroup,
		onMove,
		search
	}
) => {

	const {country: {code: countryCode}} = useContext(CountryContext);

	const intl = useIntl();
	const history = useHistory();

	const atLeastOneSelected = section.groups
		?.flatMap(g => g.documents?.filter(d => d.selected && d.visible) || [])
		?.length > 0;
	const allAreSelected = section.groups
		?.flatMap(g => g.documents?.filter(d=>d.visible) || [])
		?.every(d => d.selected) && section.groups
		?.flatMap(g => g.documents).filter(d=>d).length > 0;
	const selectedDocuments = section.groups
		?.flatMap(g => g.documents?.filter(d => d.selected && d.visible) || [])
		|| [];
	const allDocuments = section.groups
		?.flatMap(g => g.documents?.filter(d => d.visible) || [])
		|| [];

	const emptyPlaceholderData = SECTIONS_PLACEHOLDERS[sectionKey] || {};
	emptyPlaceholderData["actionOnClick"] = sectionKey === "users_documents"
		? () => setShowEditGroup({
			new: true,
			name: "",
			documents: [],
			lst_visibility: [],
			id_worksite: worksiteId
		})
		: () => history.push(`/${countryCode}/worksites/${worksiteId}`);
	const emptyPlaceholder = <EmptyStatePlaceholder {...emptyPlaceholderData} />;

	const onDragEnd = ({source, destination, draggableId}) => {
		const [type, id] = draggableId.split("-");
		const extremes = Object
			.entries({from: source, to: destination})
			.reduce(
				(acc, [key, {droppableId, index}]) => {
					const [_, id] = droppableId.split("-");
					return {...acc,  [key]: {
						id: id !== undefined ? parseInt(id) : undefined,
						index
					}};
				},
				{}
			);
		onMove({
			section: sectionKey,
			id: parseInt(id),
			type,
			...extremes,
		});
	}

	return <div className={"grid gap-y-10"}>
		<div className="grid grid-cols-2">
			<div className={"flex items-center gap-x-2"}>
				<Checkbox
					checked={allAreSelected}
					onChange={() => {
						onSelectAllSectionDocuments(sectionKey, !allAreSelected)
					}}
				/>
				<h3
					className="text-2xl font-semibold leading-7 text-black-500 md:whitespace-nowrap flex gap-4 items-center">
					<span>{section.title}</span>
				</h3>
			</div>
			<div className="justify-end flex flex-row gap-2">
				{Object.values(section.actions)?.map((a, index) => (
					<Fragment key={index}>
						{a.actionComponent(atLeastOneSelected?selectedDocuments:allDocuments, atLeastOneSelected)}
					</Fragment>
				))}
			</div>
		</div>
		{atLeastOneSelected &&
			<div className="justify-end flex items-center">
				<p
					className={"underline font-semibold text-base cursor-pointer"}
					onClick={()=>cancelSelection(sectionKey)}
				>{intl.formatMessage({id: "cancel_selection"})}</p>
			</div>}
		<div className={'flex flex-col w-full'}>
			<DragDropContext onDragEnd={onDragEnd}>
				<Droppable droppableId="groups" type="droppableItem">
					{(provided) => (
						<div ref={provided.innerRef}>
							{!section.groups && <Placeholder height="h-52" />}
							{!!section.groups && !section.groups.length && emptyPlaceholder}
							{section.groups?.map((documentGroup, index) => (
								/*
								only show the group if it has at least one visible document
								or if the group has no documents and the search is empty.
								If the search is not empty it means that the user is searching
								for something and the group should not be shown if it has no documents at all.
								On the other hand, if the group has no documents and the search is empty
								it means that the user is not searching for anything and group should be shown
								the group component will take care of showing the proper placeholder.
								 */
								documentGroup.documents?.some(d=>d.visible)
								|| (documentGroup.documents?.length===0 && search==='' || search===undefined) ?
									<Draggable
									key={`group-${documentGroup.id || index}`}
									draggableId={`group-${documentGroup.id}`}
									index={index}
								>
									{(groupProvider) => (
										<div
											ref={groupProvider.innerRef}
											{...groupProvider.draggableProps}
										>
											<Droppable droppableId={`group-${documentGroup.id}`}>
												{(provided) => (
													<div ref={provided.innerRef}>
														<DocumentsGroup
															group={documentGroup}
															groupIndex={index}
															sectionKey={sectionKey}
															alternateColor={section.alternateColor}
															onDocumentSelected={onDocumentSelected}
															dnd={!!onMove}
															dndGroupProvider={groupProvider}
															dndPlaceholder={provided.placeholder}
															onSelectAllGroupDocuments={onSelectAllGroupDocuments}
														/>
													</div>
												)}
											</Droppable>
										</div>
									)}
								</Draggable>: null
							))}
							{provided.placeholder}
						</div>
					)}
				</Droppable>
		</DragDropContext>
		</div>
	</div>
}

export default DocumentsSection;